'use client'

import { useEffect, useState } from 'react';

import { differenceInDays, format } from 'date-fns';
import { enUS, id } from 'date-fns/locale';

import useCountdown from './useCountdown';

interface AuctionDateInfo {
    dateFormatted?: string;
    timeFormatted?: string;
    daysLeft?: number;
    isToday: boolean;
    isPastTime: boolean;
    inFuture: boolean;
    countdownSeconds?: number;
    countdown?: number;
}

function getAuctionDateInfo(date: string | undefined, lang?: 'en' | 'id'): AuctionDateInfo {
    if (!date) {
        return {
            dateFormatted: undefined,
            timeFormatted: undefined,
            daysLeft: undefined,
            isToday: false,
            isPastTime: false,
            inFuture: false,
            countdownSeconds: undefined,
            countdown: undefined,
        };
    }
    const auctionDateParsed = new Date(date);

    if (isNaN(auctionDateParsed.getTime())) {
        return {
            dateFormatted: undefined,
            timeFormatted: undefined,
            daysLeft: undefined,
            isToday: false,
            isPastTime: false,
            inFuture: false,
            countdownSeconds: undefined,
            countdown: undefined,
        };
    }
    const now = new Date();
    const dateFormatted = format(auctionDateParsed, 'dd MMM yyyy', {
        locale: lang === 'en' ? enUS : id,
    });
    const timeFormatted = format(auctionDateParsed, 'HH:mm', { locale: id });
    const daysLeft = differenceInDays(auctionDateParsed, now);
    const today = daysLeft <= 1 && daysLeft >= 0;
    const inFuture = daysLeft > 0;
    const isPastTime = auctionDateParsed < now;
    let countdownSeconds;
    if (!isPastTime) {
        countdownSeconds = Math.floor(
            (auctionDateParsed.getTime() - now.getTime()) / 1000,
        );
    }
    return {
        dateFormatted,
        timeFormatted,
        daysLeft,
        isToday: today,
        inFuture,
        isPastTime,
        countdownSeconds,
        countdown: countdownSeconds,
    };
}

export default function useAuctionDate(
    auctionStartDate?: string,
    auctionEndDate?: string,
    lang?: 'en' | 'id'
) {
    // auctionStart
    const [auctionStart, setAuctionStart] = useState<AuctionDateInfo>(() =>
        getAuctionDateInfo(auctionStartDate, lang),
    );
    // auctionEnd
    const [auctionEnd, setAuctionEnd] = useState<AuctionDateInfo>(() =>
        getAuctionDateInfo(auctionEndDate, lang),
    );

    // Countdown for auctionEnd
    const [endCountdown, setEndCountdown] = useState<number | undefined>(
        auctionEnd.countdownSeconds,
    );
    const [endSeconds, startEndCountdown, , , stopEndCountdown] = useCountdown(
        auctionEnd.countdownSeconds,
    );

    const shouldShowAuctionStart =
        Boolean(auctionStartDate) && Boolean(auctionStart.daysLeft) && Boolean(auctionStart.inFuture);
    const shouldShowAuctionEnd =
        Boolean(auctionEndDate) &&
        Boolean(
            auctionStart.isPastTime &&
                ((!Boolean(auctionEnd.isPastTime) &&
                    Boolean(
                        auctionEnd.isToday &&
                            endCountdown &&
                            endCountdown > 0 &&
                            auctionEnd.countdown,
                    )) ||
                    (!Boolean(auctionEnd.isPastTime) &&
                        !Boolean(auctionEnd.isToday))),
        );

    useEffect(() => {
        setAuctionStart(getAuctionDateInfo(auctionStartDate, lang));
    }, [auctionStartDate]);

    useEffect(() => {
        const info = getAuctionDateInfo(auctionEndDate, lang);
        setAuctionEnd(info);
        setEndCountdown(info.countdownSeconds);
    }, [auctionEndDate]);

    useEffect(() => {
        if (
            auctionEnd.isToday &&
            auctionEnd.countdownSeconds &&
            auctionEnd.countdownSeconds > 0
        ) {
            startEndCountdown();
        } else {
            stopEndCountdown();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auctionEnd.countdownSeconds, auctionEnd.isToday]);

    useEffect(() => {
        if (auctionEnd.isToday) {
            setEndCountdown(endSeconds);
        }
    }, [endSeconds, auctionEnd.isToday]);

    return {
        auctionStart,
        auctionEnd,
        endCountdown,
        shouldShowAuctionStart,
        shouldShowAuctionEnd,
    };
}
